import { useState, useEffect } from "react";
import { ScanSearch, CheckCircle2, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

const STEPS = [
  { label: "Budget Bleeding", amount: "$1,845" },
  { label: "Creative Fatigue", amount: "$960" },
  { label: "Audience Overlap", amount: "$780" },
  { label: "Bidding Inefficiency", amount: "$695" },
];

export const AuditTeaser = ({ compact = false }) => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((p) => (p + 1) % 9), 1000);
    return () => clearInterval(t);
  }, []);

  const showResult = tick >= 4;
  const scanIndex = Math.min(tick, 3);

  return (
    <div
      data-testid="audit-teaser"
      className={cn(
        "rounded-2xl border border-red-500/20 bg-gradient-to-br from-red-950/30 via-[#121212] to-[#121212]",
        compact ? "p-4" : "p-5"
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <ScanSearch className="w-4 h-4 text-red-400" />
        <p className="text-red-300 text-xs font-bold tracking-widest uppercase">Free Wasted Spend Audit</p>
      </div>

      <div className="space-y-1.5 mb-3">
        {STEPS.map((s, i) => {
          const done = showResult || i < scanIndex;
          const active = !showResult && i === scanIndex;
          return (
            <div
              key={s.label}
              className={cn(
                "flex items-center justify-between rounded-lg px-3 py-1.5 text-xs transition-colors",
                active ? "bg-red-500/10 border border-red-500/30" : "bg-white/[0.03] border border-transparent"
              )}
            >
              <span className={cn("flex items-center gap-2", done ? "text-zinc-300" : active ? "text-white" : "text-zinc-500")}>
                {done ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <span className={cn("w-3.5 h-3.5 rounded-full border-2", active ? "border-red-400 border-t-transparent animate-spin" : "border-zinc-700")} />
                )}
                {s.label}
              </span>
              <span className={cn("font-semibold tabular-nums", done ? "text-red-400" : "text-zinc-700")}>
                {done ? s.amount : "—"}
              </span>
            </div>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {showResult ? (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-2.5"
            data-testid="audit-teaser-result"
          >
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-white font-bold text-base leading-tight">
                <span className="text-red-400">$4,280</span> in wasted ad spend found
              </p>
              <p className="text-zinc-400 text-xs">in the last 90 days — recoverable with ROAS-AI</p>
            </div>
          </motion.div>
        ) : (
          <motion.div key="scanning" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="px-1 py-1.5">
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-red-500 to-amber-500 rounded-full"
                animate={{ width: `${((scanIndex + 1) / 5) * 100}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <p className="text-zinc-400 text-[11px] mt-2">Scanning 90 days of ad spend — read-only, nothing is changed</p>
          </motion.div>
        )}
      </AnimatePresence>

      {!compact && (
        <p className="text-zinc-400 text-xs mt-3">
          Connect your ad accounts and see exactly where your budget is bleeding — free, in under a minute.
        </p>
      )}
    </div>
  );
};
